import { createReducer } from '@reduxjs/toolkit';
import { isUndefined, isNumber, omit } from 'lodash';
import t from '@/store/types';
import { persistReducer, purgeStoredState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

interface DashboardState {
  pageTitle: string;
  pageSubtitle: string;
  pageHint: string;
  preferencesPageTitle: string;
  sidebarExpended: boolean;
  dialogs: Record<string, any>;
  alerts: Record<string, any>;
  drawers: Record<string, any>;
  topbarEditViewId: number | null;
  requestsLoading: number;
  backLink: any;
  splashScreenLoading: number | null;
  appIsLoading: boolean;
  appIntlIsLoading: boolean;
  sidebarSubmenu: { isOpen: boolean; submenuId: string | null };
  features: Record<string, any>;
  autofill: Record<string, any>;
}

const initialState: DashboardState = {
  pageTitle: '',
  pageSubtitle: '',
  pageHint: '',
  preferencesPageTitle: '',
  sidebarExpended: true,
  dialogs: {},
  alerts: {},
  drawers: {},
  topbarEditViewId: null,
  requestsLoading: 0,
  backLink: false,
  splashScreenLoading: null,
  appIsLoading: true,
  appIntlIsLoading: true,
  sidebarSubmenu: { isOpen: false, submenuId: null },
  features: {},
  autofill: {},
};

const STORAGE_KEY = 'leyona:dashboard';

const CONFIG = {
  key: STORAGE_KEY,
  whitelist: [],
  storage,
};

const reducerInstance = createReducer(initialState, (builder) => {
  builder
    .addCase(t.CHANGE_DASHBOARD_PAGE_TITLE, (state, action: any) => {
      state.pageTitle = action.pageTitle;
    })
    .addCase(t.ALTER_DASHBOARD_PAGE_SUBTITLE, (state, action: any) => {
      state.pageSubtitle = action.pageSubtitle;
    })
    .addCase(t.CHANGE_DASHBOARD_PAGE_HINT, (state, action: any) => {
      state.pageHint = action.payload.pageHint;
    })
    .addCase(t.CHANGE_PREFERENCES_PAGE_TITLE, (state, action: any) => {
      state.preferencesPageTitle = action.pageTitle;
    })
    .addCase(t.OPEN_DIALOG, (state, action: any) => {
      state.dialogs[action.name] = {
        isOpen: true,
        payload: action.payload || {},
      };
    })
    .addCase(t.CLOSE_DIALOG, (state, action: any) => {
      state.dialogs[action.name] = {
        ...state.dialogs[action.name],
        isOpen: false,
      };
    })
    .addCase(t.OPEN_ALERT, (state, action: any) => {
      state.alerts[action.name] = {
        isOpen: true,
        payload: action.payload || {},
      };
    })
    .addCase(t.CLOSE_ALERT, (state, action: any) => {
      state.alerts[action.name] = {
        ...state.alerts[action.name],
        isOpen: false,
      };
    })
    .addCase(t.OPEN_DRAWER, (state, action: any) => {
      state.drawers[action.name] = {
        isOpen: true,
        payload: action.payload || {},
      };
    })
    .addCase(t.CLOSE_DRAWER, (state, action: any) => {
      state.drawers[action.name] = {
        ...state.drawers[action.name],
        isOpen: false,
      };
    })
    .addCase(t.CLOSE_ALL_DIALOGS, () => {})
    .addCase(t.SET_TOPBAR_EDIT_VIEW, (state, action: any) => {
      state.topbarEditViewId = action.id;
    })
    .addCase(t.SIDEBAR_EXPEND_TOGGLE, (state, action: any) => {
      const { toggle } = action.payload;
      state.sidebarExpended = isUndefined(toggle)
        ? !state.sidebarExpended
        : !!toggle;
    })
    .addCase(t.SET_DASHBOARD_BACK_LINK, (state, action: any) => {
      const { backLink } = action.payload;
      state.backLink = backLink;
    })
    .addCase(t.SPLASH_START_LOADING, (state) => {
      if (isNumber(state.splashScreenLoading)) {
        state.splashScreenLoading += 1;
      } else {
        state.splashScreenLoading = 1;
      }
    })
    .addCase(t.SET_FEATURE_DASHBOARD_META, (state, action: any) => {
      const { features } = action.payload;
      const _data: Record<string, any> = {};
      features.forEach((feature: any) => {
        _data[feature.name] = feature.is_accessible;
      });
      state.features = _data;
    })
    .addCase(t.SPLASH_STOP_LOADING, (state) => {
      state.splashScreenLoading = Math.max(
        (state.splashScreenLoading ?? 0) - 1,
        0,
      );
    })
    .addCase(t.SIDEBAR_SUBMENU_OPEN, (state, action: any) => {
      state.sidebarSubmenu.isOpen = true;
      state.sidebarSubmenu.submenuId = action.payload.submenuId;
    })
    .addCase(t.SIDEBAR_SUBMENU_CLOSE, (state) => {
      state.sidebarSubmenu.isOpen = false;
      state.sidebarSubmenu.submenuId = null;
    })
    .addCase(t.RESET, () => {
      purgeStoredState(CONFIG);
    })
    .addCase(t.ADD_AUTOFILL_REF, (state, action: any) => {
      state.autofill[action.payload.ref] = action.payload.payload || null;
    })
    .addCase(t.REMOVE_AUTOFILL_REF, (state, action: any) => {
      state.autofill = omit(state.autofill, [action.payload.ref]);
    })
    .addCase(t.RESET_AUTOFILL_REF, (state) => {
      state.autofill = {};
    });
});

export default persistReducer(CONFIG, reducerInstance);

export const getDialogPayload = (state: any, dialogName: string) => {
  return typeof state.dashboard.dialogs[dialogName] !== 'undefined'
    ? state.dashboard.dialogs[dialogName].payload
    : {};
};

export const getDialogActiveStatus = (state: any, dialogName: string) => {
  return true;
};
